import { FlightApiProvider } from './providers/FlightApiProvider';
import { AmadeusProvider } from './providers/AmadeusProvider';

/**
 * FlightAPIService - Abstraction layer for flight search APIs
 * Provides a unified interface to interact with different flight data providers
 */
export class FlightAPIService {
    constructor(provider = 'flightapi') {
        this.provider = provider;
        this.providerInstance = this.createProvider();
    }

    /**
     * Create a provider instance based on the specified provider type
     * @returns {FlightApiProvider|AmadeusProvider} Provider instance
     */
    createProvider() {
        switch(this.provider) {
            case 'flightapi':
                return new FlightApiProvider();
            case 'amadeus':
                return new AmadeusProvider();
            default:
                throw new Error(`Unsupported provider: ${this.provider}`);
        }
    }

    /**
     * Search for flights using the current provider
     * @param {Object} searchParams - Flight search parameters
     * @param {string} searchParams.origin - Origin airport code (3-letter IATA)
     * @param {string} searchParams.destination - Destination airport code (3-letter IATA)
     * @param {string} searchParams.departureDate - Departure date (YYYY-MM-DD)
     * @param {string} searchParams.returnDate - Return date (YYYY-MM-DD)
     * @param {number} searchParams.adults - Number of adult passengers
     * @param {string} searchParams.currency - Currency code (e.g., "CAD$", "USD$")
     * @returns {Promise<Object>} Normalized flight search results
     */
    async searchFlights(searchParams) {
        try {
            const result = await this.providerInstance.search(searchParams);
            return result;
        } catch (error) {
            console.error(`Flight search error with ${this.provider} provider:`, error);
            throw error;
        }
    }

    /**
     * Switch to a different provider
     * @param {string} provider - Provider name ('flightapi' or 'amadeus')
     */
    setProvider(provider) {
        if (this.provider === provider) {
            return; // No change needed
        }

        this.provider = provider;
        this.providerInstance = this.createProvider();
        console.log(`Switched to ${provider} provider`);
    }

    /**
     * Get the current provider name
     * @returns {string} Current provider name
     */
    getCurrentProvider() {
        return this.provider;
    }

    /**
     * Get available providers
     * @returns {string[]} Array of available provider names
     */
    getAvailableProviders() {
        return ['flightapi', 'amadeus'];
    }

    /**
     * Check if a provider is available
     * @param {string} provider - Provider name to check
     * @returns {boolean} True if provider is available
     */
    isProviderAvailable(provider) {
        return this.getAvailableProviders().includes(provider);
    }

    /**
     * Get provider display information
     * @returns {Object} Provider information
     */
    getProviderInfo() {
        const providers = {
            flightapi: {
                name: 'FlightApi.io',
                description: 'Current production flight data provider',
                status: 'stable'
            },
            amadeus: {
                name: 'Amadeus',
                description: 'Alternative flight data provider (experimental)',
                status: 'experimental'
            }
        };

        return providers[this.provider] || null;
    }
}

// Export a default instance for easy use
export const flightAPIService = new FlightAPIService();